import React from 'react';
import { TouristSite } from '../types';
import { X, Save } from 'lucide-react';

interface SiteEditModalProps {
  site: TouristSite;
  serverSiteIds: Set<string>;
  onSave: (site: TouristSite) => void;
  onClose: () => void;
}

export const SiteEditModal: React.FC<SiteEditModalProps> = React.memo(({ site, serverSiteIds, onSave, onClose }) => {
  const [editingSite, setEditingSite] = React.useState<TouristSite>({ ...site });
  const isExistingOnServer = serverSiteIds.has(site.id);

  const updateField = React.useCallback((field: keyof TouristSite, value: any) => {
    setEditingSite(prev => ({ ...prev, [field]: value }));
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-6xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-black text-zinc-900 uppercase tracking-widest">Modifier {site.name}</h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors">
            <X className="w-6 h-6 text-zinc-400" />
          </button>
        </div>
        
        {isExistingOnServer && (
          <div className="mx-6 mt-6 bg-amber-50 border border-amber-200 p-4 rounded-2xl flex items-start gap-3">
            <div className="bg-amber-100 p-2 rounded-xl">
              <Save className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h4 className="font-bold text-amber-900 text-sm uppercase tracking-wider">Règle de Publication Strict</h4>
              <p className="text-amber-800 text-xs mt-1 leading-relaxed">
                Ce site existe déjà sur le serveur Ionos. Pour garantir l'intégrité des données enrichies (IA, prix, Google), toute modification doit être effectuée via l'interface d'administration du serveur.
              </p>
            </div>
          </div>
        )}

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Informations de base */}
          <div className="space-y-4">
            <h3 className="font-bold text-zinc-900">Informations de base</h3>
            <input type="text" value={editingSite.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Nom" className="w-full p-3 bg-zinc-50 border rounded-xl" />
            <textarea value={editingSite.description} onChange={(e) => updateField('description', e.target.value)} placeholder="Description" className="w-full p-3 bg-zinc-50 border rounded-xl" />
            <input type="text" value={editingSite.address || ''} onChange={(e) => updateField('address', e.target.value)} placeholder="Adresse" className="w-full p-3 bg-zinc-50 border rounded-xl" />
            <input type="text" value={editingSite.phone || ''} onChange={(e) => updateField('phone', e.target.value)} placeholder="Téléphone" className="w-full p-3 bg-zinc-50 border rounded-xl" />
            <input type="text" value={editingSite.website || ''} onChange={(e) => updateField('website', e.target.value)} placeholder="Site Web" className="w-full p-3 bg-zinc-50 border rounded-xl" />
          </div>

          {/* Tarifs */}
          <div className="space-y-4">
            <h3 className="font-bold text-zinc-900">Tarifs</h3>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editingSite.isFree} onChange={(e) => updateField('isFree', e.target.checked)} /> Gratuit</label>
            <input type="number" value={editingSite.adultPrice || 0} onChange={(e) => updateField('adultPrice', parseFloat(e.target.value))} placeholder="Prix Adulte" className="w-full p-3 bg-zinc-50 border rounded-xl" />
            <input type="number" value={editingSite.child6_12Price || 0} onChange={(e) => updateField('child6_12Price', parseFloat(e.target.value))} placeholder="Prix Enfant 6-12" className="w-full p-3 bg-zinc-50 border rounded-xl" />
            <input type="number" value={editingSite.child3_6Price || 0} onChange={(e) => updateField('child3_6Price', parseFloat(e.target.value))} placeholder="Prix Enfant 3-6" className="w-full p-3 bg-zinc-50 border rounded-xl" />
            <input type="number" value={editingSite.babyPrice || 0} onChange={(e) => updateField('babyPrice', parseFloat(e.target.value))} placeholder="Prix Bébé" className="w-full p-3 bg-zinc-50 border rounded-xl" />
          </div>

          {/* Spécificités */}
          <div className="space-y-4">
            <h3 className="font-bold text-zinc-900">Spécificités</h3>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editingSite.isCatering} onChange={(e) => updateField('isCatering', e.target.checked)} /> Restaurateur (Snack/Resto)</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editingSite.isAccommodation} onChange={(e) => updateField('isAccommodation', e.target.checked)} /> Hébergement</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editingSite.isHandicapAccessible} onChange={(e) => updateField('isHandicapAccessible', e.target.checked)} /> Accessible PMR</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editingSite.dogsNotAllowed} onChange={(e) => updateField('dogsNotAllowed', e.target.checked)} /> Chiens interdits</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editingSite.isWindingRoad} onChange={(e) => updateField('isWindingRoad', e.target.checked)} /> Route sinueuse</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!editingSite.notSuitableForChildren} onChange={(e) => updateField('notSuitableForChildren', e.target.checked)} /> Inadapté enfants</label>
            <input type="text" value={editingSite.openingHours || ''} onChange={(e) => updateField('openingHours', e.target.value)} placeholder="Horaires" className="w-full p-3 bg-zinc-50 border rounded-xl" />
          </div>
          {/* Images (Lazy loaded) */}
          <div className="space-y-4">
            <h3 className="font-bold text-zinc-900">Photos</h3>
            {editingSite.images && editingSite.images.filter(url => !url.includes('maps.googleapis.com')).length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {editingSite.images.filter(url => !url.includes('maps.googleapis.com')).map((url, index) => (
                  <img 
                    key={index} 
                    src={url} 
                    alt={`${editingSite.name} ${index + 1}`} 
                    className="w-full h-24 object-cover rounded-lg"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                ))}
              </div>
            ) : (
              <p className="text-zinc-400 text-sm">Aucune photo disponible.</p>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-zinc-100 flex justify-end gap-4">
          <button onClick={onClose} className="px-6 py-3 bg-zinc-100 text-zinc-600 rounded-xl font-bold">Annuler</button>
          <button 
            disabled={isExistingOnServer}
            onClick={() => onSave(editingSite)} 
            className={`px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all ${
              isExistingOnServer 
                ? "bg-zinc-100 text-zinc-400 cursor-not-allowed" 
                : "bg-emerald-600 text-white hover:bg-emerald-700"
            }`}
          >
            <Save className="w-4 h-4" /> 
            {isExistingOnServer ? "MODIFICATION INTERDITE (EXISTANT)" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
});
